import { AccountIconSVG } from 'components/iconsSVG/accountIcon';
import { Button } from 'components/ui/button';
import { Popover } from 'components/ui/popover';
import { useI18n } from 'context/i18n';
import { useUser } from 'context/user';
import { useLink, useLocale } from 'hooks';
import { Heading } from 'react-aria-components';
import { PowerSVG } from 'components/iconsSVG/power';
import { Skeleton } from 'components/ui/skeleton';
import { Link } from 'components/ui/link';
import { isDomain } from 'utils/domain';
import { BoxSVG } from 'components/iconsSVG/box';
import { TouchTarget } from 'components/ui/touch-target';
import { RadixPopover, RadixPopoverContent, RadixPopoverTrigger } from 'components/ui/radix-popover';
import { canUserHover } from 'utils';
import useDelayedState from 'hooks/useDelayedState';
import { useCallback } from 'react';

// AccountMenu.i18Name = 'AcccountMenu'
type AccountMenuProps = {
  onClickYourOrders: () => void;
  onClickSignIn: () => void;
};

export function AccountMenu({ onClickYourOrders, onClickSignIn }: AccountMenuProps) {
  const { userData, loading, logout } = useUser();
  const { t, profileData } = useI18n();
  const { country } = useLocale();
  const to = useLink();
  const [open, setOpen] = useDelayedState(false);

  const onHoverStart = useCallback(() => {
    const canHover = canUserHover();
    if (!canHover) return;
    setOpen(true, 0);
  }, [setOpen]);

  const onHoverEnd = useCallback(() => {
    const canHover = canUserHover();
    if (!canHover) return;
    setOpen(false, 500);
  }, [setOpen]);

  if (profileData.global_e) {
    return (
      <Button variant="none" className="accountButton flex max-lg:hidden nnormal:[@media(max-width:1200px)]:hidden" onPress={onClickYourOrders}>
        <span className="flex items-center">
          <BoxSVG className="h-5 2xl:hidden" />
          <span className="font-moderne text-xs capitalize underline md:sr-only 2xl:not-sr-only">{t('menu.superior', 'mis.pedidos')}</span>
        </span>
      </Button>
    );
  }

  return (
    <div className="flex gap-2 max-md:hidden">
      <RadixPopover open={open} onOpenChange={(open) => setOpen(open, 0)}>
        <div onMouseEnter={onHoverStart} onMouseLeave={onHoverEnd}>
          <RadixPopoverTrigger asChild>
            <Button variant="none" className="relative flex">
              <TouchTarget className="size-7" hideOnFinePointer={false} />
              <span className="flex items-center">
                <AccountIconSVG className="text-copy nnormal:text-inverted 2xl:hidden" />
                <span className="sr-only font-moderne uppercase 2xl:not-sr-only">{t('mi.cuenta', 'mi.cuenta')}</span>
              </span>
            </Button>
          </RadixPopoverTrigger>
          <RadixPopoverContent align="end" side="bottom" alignOffset={-10} className="w-[250px]">
            {loading ?
              <div>
                <Skeleton className="mb-2 h-4 w-36" />
                <div className="my-2 h-px bg-faint" />
                <Skeleton className="mb-0.5 h-8 w-full" />
                <Skeleton className="mb-0.5 h-8 w-full" />
                <Skeleton className="h-8 w-full" />
                <div className="my-2 h-px bg-faint" />
                <Skeleton className="h-8 w-full" />
              </div>
            : userData === null ?
              <div className="flex flex-col gap-8 nnormal:gap-12">
                {/* Deslogueados */}
                <div className="flex flex-col">
                  <Heading level={2} className="mb-2 text-lg font-normal nnormal:font-secondary">
                    {t('mi.cuenta', 'mi.cuenta.titulo')}
                  </Heading>
                  <p className="mb-0 text-sm">{t('mi.cuenta', 'identificate.aprovecha.ventajas')}</p>
                  <Button slot="close" onPress={onClickSignIn} className="mt-3 nnormal:font-secondary">
                    {t('mi.cuenta', 'identificate')}
                  </Button>
                </div>
                <div className="flex flex-col">
                  <Heading level={2} className="mb-2 text-lg font-normal nnormal:font-secondary">
                    {t('mi.cuenta', 'mis.pedidos', 'Orders')}
                  </Heading>
                  <p className="mb-0 text-sm">{t('mi.cuenta', country === 'JP' ? 'manage.an.order.no.exchange' : 'manage.an.order')}</p>
                  <Button slot="close" variant="outline" onPress={onClickYourOrders} className="mt-3 nnormal:font-secondary">
                    {t('menu.superior', 'mis.pedidos')}
                  </Button>
                </div>
              </div>
            : <div className="flex flex-col">
                {/* Logueados */}
                <Heading level={2} className="mb-0 text-xs font-normal uppercase">
                  <span className="">{`${t('mi.cuenta', 'hola', 'Hello')}, `}</span>
                  <span className="font-bold nnormal:font-secondary">{userData?.account?.username || userData?.account?.email || ''}</span>
                </Heading>
                <div className="my-2 h-px bg-faint" />
                <div className="flex flex-col gap-0.5">
                  <Link
                    variant="none"
                    className="-mx-1 rounded px-1 py-1.5 text-sm hover:bg-dim pressed:bg-dim"
                    href={to(`/myaccount${isDomain('nnormal') ? '/nnormal-world' : ''}`)}
                  >
                    {t('mi.cuenta', 'mi.cuenta', 'My Account')}
                  </Link>
                  <Link variant="none" className="-mx-1 rounded px-1 py-1.5 text-sm hover:bg-dim pressed:bg-dim" href={to(`/myaccount/myorders`)}>
                    {t('mi.cuenta', 'mi.cuenta.pedidos.y.devoluciones', 'My Orders & Returns')}
                  </Link>
                  <Link variant="none" className="-mx-1 rounded px-1 py-1.5 text-sm hover:bg-dim pressed:bg-dim" href={to(`/myaccount/myprofile`)}>
                    {t('mi.cuenta', 'mi.perfil', 'My Profile')}{' '}
                  </Link>
                </div>
                <div className="my-2 h-px bg-faint" />
                <Button
                  variant="none"
                  className="-mx-1 rounded px-1 py-1.5 text-sm hover:bg-dim pressed:bg-dim [&>span]:w-full [&>span]:justify-between"
                  onPress={logout}
                >
                  {t('mi.cuenta', 'logout', 'Logout')}
                  <PowerSVG />
                </Button>
              </div>
            }
          </RadixPopoverContent>
        </div>
      </RadixPopover>
    </div>
  );
}
